import { useQuery } from "@tanstack/react-query";
import { RotateCcw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import ExpandableSection from "@/components/job/ExpandableSection";
import { Button } from "@/components/ui/button";
import { createEditJobOptionsQuery } from "@/hooks/useConfigOptions";
import useCUMechanicalJobStore from "@/store/useCUMechanicalJobStore";
import { filterMaterialGroupsByDesignCode } from "@/utils/materialFilters";

import { AddStrongbacksQuestion } from "./AddStrongbacksQuestion";
import { MatchMaterialsQuestion } from "./MatchMaterialsQuestion";
import { type StrongbackSchema, StrongbacksForm } from "./StrongbacksForm";

const cardName = "strongbacks";

const findMaterialLabel = (
  value: string | undefined,
  groups?: Array<{
    group: string;
    options: Array<{
      id: number;
      material: string;
      type: string;
    }>;
  }>
) => {
  if (!value || !groups) {
    return value;
  }
  const numericValue = Number(value);
  for (const group of groups) {
    const match = group.options.find((option) => option.id === numericValue);
    if (match) {
      return `${match.material} - ${match.type}`;
    }
  }
  return value;
};

export function StrongbackDetails() {
  const [addMore, setAddMore] = useState(false);
  const [matchMaterials, setMatchMaterials] = useState<boolean | undefined>(undefined);
  const { sections, toggleExpandableSection, setStrongbacks } = useCUMechanicalJobStore();
  const savedStrongbacks = sections.strongbacks;
  // Whether the yes/no question has been answered at all (true even when the answer was "no"),
  // so we don't re-show the unanswered question after loading a job that opted out, and can
  // still offer a reset.
  const showReset = savedStrongbacks.isDataFilled;
  const matchFlag = savedStrongbacks.data.match_strongback_to_enclosure;
  const designCode = sections.jobDetails.data.design_code;

  const materialsComplete =
    matchFlag === true ||
    (matchFlag === false && !!savedStrongbacks.data.strongbacks && !!savedStrongbacks.data.strongbacks_stud_bolts);

  const createOptions = useQuery({
    ...createEditJobOptionsQuery,
    select(response) {
      return {
        strongbackOptions: response.strongbacks,
        strongbackStudBoltOptions: response.strongbacks_stud_bolts,
      };
    },
  });
  const filteredStrongbackOptions = useMemo(
    () => filterMaterialGroupsByDesignCode(createOptions.data?.strongbackOptions, designCode),
    [createOptions.data?.strongbackOptions, designCode]
  );
  const filteredStrongbackStudBoltOptions = useMemo(
    () => filterMaterialGroupsByDesignCode(createOptions.data?.strongbackStudBoltOptions, designCode),
    [createOptions.data?.strongbackStudBoltOptions, designCode]
  );

  useEffect(() => {
    // Initialize state based on saved data
    setAddMore(savedStrongbacks.addMore || false);
    setMatchMaterials(matchFlag);
  }, [savedStrongbacks.addMore, matchFlag]);

  function handleSaveStrongbacks(formData: StrongbackSchema) {
    setStrongbacks({
      data: {
        ...savedStrongbacks.data,
        ...formData,
        match_strongback_to_enclosure: false,
      },
      status: "done",
      addMore: true,
    });
  }

  function handleNoStrongbacks() {
    setStrongbacks({
      data: {
        strongbacks: "",
        strongbacks_stud_bolts: "",
        match_strongback_to_enclosure: undefined,
      },
      status: "done",
      addMore: false,
    });
  }

  function handleYesMatchMaterials() {
    setStrongbacks({
      data: {
        ...savedStrongbacks.data,
        match_strongback_to_enclosure: true,
      },
      status: "done",
      addMore: true,
    });
    setMatchMaterials(true);
    setAddMore(true);
  }

  function handleNoMatchMaterials() {
    setStrongbacks({
      data: {
        ...savedStrongbacks.data,
        match_strongback_to_enclosure: false,
      },
      status: "open",
      addMore: true,
    });
    setMatchMaterials(false);
    setAddMore(true);
  }

  function handleYesAddStrongbacks() {
    setStrongbacks({
      data: {
        ...savedStrongbacks.data,
      },
      status: "open",
      addMore: true,
    });
    setAddMore(true);
  }

  function handleResetStrongbacks() {
    setAddMore(false);
    setMatchMaterials(undefined);
    setStrongbacks({
      data: {
        strongbacks: "",
        strongbacks_stud_bolts: "",
        match_strongback_to_enclosure: undefined,
      },
      status: "open",
      addMore: false,
    });
  }

  const data = savedStrongbacks.data;

  const summaryItems = useMemo(() => {
    if (!materialsComplete) {
      return [];
    }
    if (matchFlag) {
      return [{ label: "Materials", value: "Matched to enclosure materials" }];
    }
    return [
      { label: "Strongbacks", value: findMaterialLabel(data.strongbacks, filteredStrongbackOptions) },
      {
        label: "Stud Bolts",
        value: findMaterialLabel(data.strongbacks_stud_bolts, filteredStrongbackStudBoltOptions),
      },
    ]
      .filter((item) => item.value)
      .map((item) => item as { label: string; value: string });
  }, [
    data.strongbacks,
    data.strongbacks_stud_bolts,
    filteredStrongbackOptions,
    filteredStrongbackStudBoltOptions,
    matchFlag,
    materialsComplete,
  ]);

  return (
    <ExpandableSection
      setIsExpanded={() => toggleExpandableSection(cardName)}
      status={savedStrongbacks.status}
      title="Strongback Material Details"
    >
      {showReset ? (
        <div className="mb-4 flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            className="gap-2 text-gray-600 hover:text-gray-900"
            onClick={handleResetStrongbacks}
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
        </div>
      ) : null}

      {!addMore && !showReset && <AddStrongbacksQuestion onNo={handleNoStrongbacks} onYes={handleYesAddStrongbacks} />}

      {!addMore && showReset && <p className="text-gray-600 text-sm">No strongbacks added to design.</p>}

      {addMore && matchMaterials === undefined && (
        <MatchMaterialsQuestion onNo={handleNoMatchMaterials} onYes={handleYesMatchMaterials} />
      )}

      {addMore && matchMaterials === false && !materialsComplete && (
        <StrongbacksForm
          onSave={handleSaveStrongbacks}
          defaultValues={{
            strongbacks: data.strongbacks ?? "",
            strongbacks_stud_bolts: data.strongbacks_stud_bolts ?? "",
            match_strongback_to_enclosure: false,
          }}
        />
      )}

      {addMore && matchMaterials !== undefined && materialsComplete ? (
        <div className="flex flex-col gap-2 text-gray-600 text-sm">
          <p>Material selection saved.</p>
          {summaryItems.length > 0 ? (
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-700">
              {summaryItems.map((item) => (
                <div key={item.label} className="flex flex-wrap gap-1">
                  <span className="font-medium">{item.label}:</span>
                  <span>{item.value}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </ExpandableSection>
  );
}
